import { useRef, useState } from 'react'
import { ScanLine, Camera, X, Loader } from 'lucide-react'
import { Button } from './Button'
import { Modal } from './Modal'

interface BarcodeScannerProps {
  onScan: (value: string) => void
  label?: string
}

// BarcodeDetector is a browser API, not in TypeScript types by default
declare global {
  class BarcodeDetector {
    constructor(options?: { formats?: string[] })
    detect(image: ImageBitmapSource): Promise<Array<{ rawValue: string; format: string }>>
    static getSupportedFormats(): Promise<string[]>
  }
}

export function BarcodeScanner({ onScan, label = 'Scannen' }: BarcodeScannerProps) {
  const [open, setOpen] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const [stream, setStream] = useState<MediaStream | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const supported = typeof window !== 'undefined' && 'BarcodeDetector' in window

  // File-based fallback: pick image from camera roll / camera
  const scanFromFile = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.capture = 'environment'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file || !('BarcodeDetector' in window)) return
      setScanning(true)
      setError('')
      try {
        const detector = new BarcodeDetector()
        const bitmap = await createImageBitmap(file)
        const codes = await detector.detect(bitmap)
        if (codes.length > 0) {
          onScan(codes[0].rawValue)
          setOpen(false)
        } else {
          setError('Kein Barcode gefunden. Bitte erneut versuchen.')
        }
      } catch {
        setError('Scan fehlgeschlagen')
      } finally {
        setScanning(false)
      }
    }
    input.click()
  }

  const startLiveScan = async () => {
    setError('')
    setScanning(true)
    try {
      const ms = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      })
      setStream(ms)
      if (videoRef.current) {
        videoRef.current.srcObject = ms
        await videoRef.current.play()
      }
      const detector = new BarcodeDetector()
      intervalRef.current = setInterval(async () => {
        if (!videoRef.current || !canvasRef.current) return
        const canvas = canvasRef.current
        canvas.width = videoRef.current.videoWidth
        canvas.height = videoRef.current.videoHeight
        canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0)
        try {
          const codes = await detector.detect(canvas)
          if (codes.length > 0) {
            stopLiveScan()
            onScan(codes[0].rawValue)
            setOpen(false)
          }
        } catch { /* continue scanning */ }
      }, 400)
    } catch {
      setError('Kamera-Zugriff verweigert oder nicht verfügbar.')
      setScanning(false)
    }
  }

  const stopLiveScan = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    stream?.getTracks().forEach((t) => t.stop())
    setStream(null)
    setScanning(false)
  }

  const handleOpen = () => {
    setOpen(true)
    setError('')
    if (supported) {
      startLiveScan()
    }
  }

  const handleClose = () => {
    stopLiveScan()
    setOpen(false)
  }

  // If BarcodeDetector not supported, use file input directly
  if (!supported) {
    return (
      <button
        type="button"
        onClick={scanFromFile}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
        title="Barcode scannen"
      >
        <ScanLine size={20} />
      </button>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
        title="Barcode scannen"
      >
        <ScanLine size={20} />
      </button>

      <Modal open={open} onClose={handleClose} title="Seriennummer scannen"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={scanFromFile} icon={<Camera size={16} />}>
              Foto wählen
            </Button>
            <Button variant="outline" fullWidth onClick={handleClose} icon={<X size={16} />}>
              Abbrechen
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="relative bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <canvas ref={canvasRef} className="hidden" />
            {/* Scan overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-32 border-2 border-blue-400 rounded-xl">
                <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-blue-400 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-blue-400 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-blue-400 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-blue-400 rounded-br-lg" />
                {/* Scan line animation */}
                <div className="absolute inset-x-0 border-t-2 border-blue-400 opacity-70 animate-scan-line" style={{ top: '50%' }} />
              </div>
            </div>
            {scanning && !stream && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader size={32} className="text-white animate-spin" />
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 rounded-xl text-sm text-red-700">{error}</div>
          )}

          <p className="text-sm text-gray-500 text-center">
            Barcode / QR-Code in den Rahmen halten
          </p>
        </div>
      </Modal>
    </>
  )
}
